export function Stat({
  label,
  value,
  hint,
  delta,
}: {
  label: string;
  value: string;
  hint?: string;
  // Comparison against the previous window. Provided pre-formatted by
  // the caller because the meaningful representation depends on the
  // metric (percentage points for rates, absolute count for messages).
  // `direction` controls the arrow + color; `neutral` skips the
  // styling for cases where direction has no qualitative meaning.
  delta?: {
    text: string;
    direction: "up" | "down" | "neutral";
    // For rates, "up" is good (more passing). For counts (messages),
    // "up" is just informational. Caller hints the polarity.
    polarity?: "good-up" | "good-down" | "neutral";
  };
}) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">{value}</div>
      {delta ? <DeltaLine delta={delta} /> : null}
      {hint ? <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</div> : null}
    </div>
  );
}

function DeltaLine({
  delta,
}: {
  delta: NonNullable<Parameters<typeof Stat>[0]["delta"]>;
}) {
  const polarity = delta.polarity ?? "neutral";
  const isGood =
    (polarity === "good-up" && delta.direction === "up") ||
    (polarity === "good-down" && delta.direction === "down");
  const isBad =
    (polarity === "good-up" && delta.direction === "down") ||
    (polarity === "good-down" && delta.direction === "up");

  const color = isGood
    ? "text-emerald-700 dark:text-emerald-300"
    : isBad
    ? "text-red-700 dark:text-red-300"
    : "text-slate-500 dark:text-slate-400";

  const arrow =
    delta.direction === "up" ? "↑" : delta.direction === "down" ? "↓" : "→";

  return (
    <div className={`mt-1 text-xs ${color}`}>
      {arrow} {delta.text}
    </div>
  );
}
